import { format } from "date-fns/format";
import { isToday } from "date-fns/isToday";
import { nb } from "date-fns/locale/nb";
import DashedLine from "features/CenterContent/VehicleDetails/shared/DashedLine";
import { getAbbreviationForRole } from "features/TodoOploggSidebar/common/utils";
import { ReadReceipts } from "features/TodoOploggSidebar/Oplogg/OploggElement/ReadReceipt";
import {
  IndicatorWrapper,
  LogItem,
  VerticalLine,
  VerticalLineForHighlightedBox,
} from "features/TodoOploggSidebar/Oplogg/OploggElement/styles";
import { DropsLogEntry } from "features/TodoOploggSidebar/Oplogg/OploggElement/types";
import {
  getAge,
  getAgeColor,
  getAgeColorForVerticalLine,
  getSubTextColor,
  getTextColor,
} from "features/TodoOploggSidebar/Oplogg/OploggElement/utils";
import { FC, useEffect, useRef, useState } from "react";
import { CircleIndicator } from "shared/components/dataDisplay/CircleIndicator";
import { HStack, Text } from "shared/components/Spor";
import { useElementVisibilityState } from "shared/hooks/useIsElementCurrentlyVisible";
import { useUserInfo } from "shared/hooks/useUserInfo";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

type OploggElementProps = {
  logEntry: DropsLogEntry;
  isHighlighted: boolean;
  onClick: () => void;
  readonly: boolean;
};

export const OploggElement: FC<OploggElementProps> = ({
  logEntry,
  isHighlighted,
  onClick,
  readonly,
}) => {
  const {
    uuid,
    author,
    reportedTime,
    text,
    originSystem,
    originSystemHumanReadable,
    receipts,
  } = logEntry;
  // reportedTime is null if the log was not successfully sent to cim
  // in that case, show the current time with "*"
  const reportedDateTime = new Date(reportedTime ?? new Date());
  const displayedDateTime =
    format(reportedDateTime, "HH:mm", { locale: nb }) +
    (reportedTime ? "" : "*");

  const { email, displayName } = useUserInfo();
  const isItemFromThisUser = email === author || displayName === author;
  const { role: currentRole } = useDropsRole();
  const currentRoleAbbreviation = getAbbreviationForRole(currentRole);
  const readByThisUser = receipts?.includes(currentRoleAbbreviation);

  const [actualAge, setAge] = useState(getAge(reportedDateTime));

  const age = readByThisUser || isItemFromThisUser ? "OLD" : actualAge;
  const [hasElementBeenVisible, setHasElementBeenVisible] = useState(false);
  const isFromToday = isToday(reportedDateTime);

  const isNew = age === "NEW";

  const isAlmostNew = age === "ALMOST_NEW";

  const ref = useRef<HTMLLIElement>(null);
  const elementVisibilityState = useElementVisibilityState(ref);

  const theme = useTheme();

  useEffect(() => {
    let interval: any;
    if (age === "NEW" || age === "ALMOST_NEW") {
      interval = setInterval(() => {
        setAge(getAge(reportedDateTime));
      }, 10000); // check age every 10 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (hasElementBeenVisible === true) {
      return;
    }

    setHasElementBeenVisible(elementVisibilityState.isVisible);
  }, [elementVisibilityState]);

  const getAuthor = () => {
    if (originSystem === "DROPS_DASHBOARD")
      return author?.split("@")[0] ?? originSystemHumanReadable;
    return author
      ? `${author} (${originSystemHumanReadable})`
      : originSystemHumanReadable;
  };

  const showHighlighted = isHighlighted && !readonly;
  const showNewIndicator =
    isNew && !hasElementBeenVisible && !isItemFromThisUser;
  const showBookmarkedIndicator =
    showHighlighted && !elementVisibilityState.isVisible;

  return (
    <>
      <LogItem
        data-new={isNew || undefined}
        data-almost-new={isAlmostNew || undefined}
        data-show-new-indicator={showNewIndicator || undefined}
        data-bookmarked={showHighlighted || undefined}
        data-show-bookmarked-indicator={showBookmarkedIndicator || undefined}
        ref={ref}
        onClick={readonly ? undefined : onClick}
        $readonly={readonly}
        $hover={
          isNew ? theme.colorAlarmFillPulse : theme.colorBackgroundSecondary
        }
        $isSemantic={isNew || isAlmostNew}
      >
        <IndicatorWrapper>
          <VerticalLine $color={getAgeColorForVerticalLine(age, theme)} />
          <CircleIndicator $color={getAgeColor(age, theme)} />
          <VerticalLine $color={getAgeColorForVerticalLine(age, theme)} />
        </IndicatorWrapper>
        <Text
          variant="sm"
          fontWeight="bold"
          style={{
            color: getTextColor(isNew, isAlmostNew, readonly, theme),
          }}
        >
          {displayedDateTime}
        </Text>
        <Text
          variant="sm"
          style={{
            color: getTextColor(isNew, isAlmostNew, readonly, theme),
            gridColumn: "3",
            wordBreak: "break-word",
          }}
        >
          {text}
        </Text>
        <HStack
          gridColumn={3}
          justifyContent="flex-end"
          gap="6px"
          alignSelf="stretch"
        >
          <Text
            variant="xxs"
            style={{
              color: getSubTextColor(isNew, isAlmostNew, readonly, theme),
              textAlign: "end",
              fontSize: "14px", // XXS doesn't actually exist in spor-react
              lineHeight: "18px", // XXS doesn't actually exist in spor-react
            }}
          >
            {getAuthor()}
          </Text>
          {!isFromToday && (
            <Text
              variant="xxs"
              style={{
                color: getSubTextColor(isNew, isAlmostNew, readonly, theme),
                textAlign: "end",
                fontSize: "14px", // XXS doesn't actually exist in spor-react
                lineHeight: "18px", // XXS doesn't actually exist in spor-react
              }}
            >
              {format(reportedDateTime, "dd.MM", { locale: nb })}
            </Text>
          )}
        </HStack>

        <ReadReceipts
          readReceipts={receipts}
          entryUuid={uuid}
          isNew={isNew && !readonly}
          isAlmostNew={isAlmostNew && !readonly}
        />
      </LogItem>
      {showHighlighted && (
        <HStack
          style={{
            alignItems: "flex-start",
            height: 0,
          }}
        >
          <VerticalLineForHighlightedBox color={theme.colorOutlineMain} />
        </HStack>
      )}
      {!isNew && !isAlmostNew && <DashedLine variant="secondary" />}
    </>
  );
};
